import apiClient, { getApiErrorMessage } from './apiClient';
import { normalizeConversation } from './conversations.api';

const unwrapApiData = (data) => data?.data || data?.Data || data;
const unwrapApiList = (data) => {
  const unwrapped = unwrapApiData(data);
  if (Array.isArray(unwrapped)) return unwrapped;
  if (Array.isArray(unwrapped?.items)) return unwrapped.items;
  if (Array.isArray(unwrapped?.Items)) return unwrapped.Items;
  return [];
};

const parseFilesJson = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (typeof raw !== 'string' || !raw.trim()) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const normalizeFolder = (item) => {
  const filesJson = item?.FilesJson || item?.filesJson || '[]';
  const conversationsRaw =
    item?.Conversations || item?.conversations || null;

  return {
    id: item?.Id || item?.id,
    name: item?.Name || item?.name || '',
    description: item?.Description || item?.description || '',
    color: item?.Color || item?.color || null,
    icon: item?.Icon || item?.icon || null,
    instructions: item?.Instructions || item?.instructions || '',
    filesJson,
    files: parseFilesJson(filesJson),
    conversations: Array.isArray(conversationsRaw)
      ? conversationsRaw.map(normalizeConversation).filter((c) => c.id)
      : undefined,
    createdAt: item?.CreatedAt || item?.createdAt || new Date().toISOString(),
    updatedAt: item?.UpdatedAt || item?.updatedAt || new Date().toISOString()
  };
};

export const fetchFoldersApi = async () => {
  try {
    const { data } = await apiClient.get('/folders');
    return unwrapApiList(data).map(normalizeFolder).filter((item) => item.id);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to load chat projects.'));
  }
};

export const fetchFolderApi = async (id) => {
  try {
    const { data } = await apiClient.get(`/folders/${encodeURIComponent(id)}`);
    return normalizeFolder(unwrapApiData(data));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to load chat project.'));
  }
};

export const createFolderApi = async ({
  name,
  description = '',
  color,
  icon,
  instructions,
  filesJson
} = {}) => {
  try {
    const payload = {
      Name: name,
      Description: description || '',
      Color: color || null,
      Icon: icon || null,
      Instructions: instructions || ''
    };
    if (filesJson !== undefined) {
      payload.FilesJson = filesJson;
    }
    const { data } = await apiClient.post('/folders', payload);
    return normalizeFolder(unwrapApiData(data));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to create chat project.'));
  }
};

export const updateFolderApi = async (
  id,
  { name, description, color, icon, instructions, filesJson } = {}
) => {
  try {
    const payload = {};
    if (name !== undefined) payload.Name = name;
    if (description !== undefined) payload.Description = description;
    if (color !== undefined) payload.Color = color;
    if (icon !== undefined) payload.Icon = icon;
    if (instructions !== undefined) payload.Instructions = instructions;
    if (filesJson !== undefined) payload.FilesJson = filesJson;
    const { data } = await apiClient.put(`/folders/${encodeURIComponent(id)}`, payload);
    return normalizeFolder(unwrapApiData(data));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to update chat project.'));
  }
};

export const updateFolderFilesApi = async (id, files) => {
  try {
    const filesJson = JSON.stringify(Array.isArray(files) ? files : []);
    const { data } = await apiClient.put(`/folders/${encodeURIComponent(id)}/files`, {
      FilesJson: filesJson
    });
    return normalizeFolder(unwrapApiData(data));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to update chat project files.'));
  }
};

export const deleteFolderApi = async (id) => {
  try {
    await apiClient.delete(`/folders/${encodeURIComponent(id)}`);
    return id;
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to delete chat project.'));
  }
};

export const fetchFolderConversationsApi = async (folderId) => {
  try {
    const { data } = await apiClient.get(`/folders/${encodeURIComponent(folderId)}/conversations`);
    return unwrapApiList(data).map(normalizeConversation).filter((item) => item.id);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to load chat project conversations.'));
  }
};

export const createConversationInFolderApi = async (
  folderId,
  { title = '', model = '', provider = '', isTemporary = false } = {}
) => {
  try {
    const payload = {};
    if (title) payload.Title = title;
    if (model) payload.Model = model;
    if (provider) payload.Provider = provider;
    if (isTemporary) payload.IsTemporary = true;
    const { data } = await apiClient.post(
      `/folders/${encodeURIComponent(folderId)}/conversations`,
      payload
    );
    return normalizeConversation(unwrapApiData(data));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to create conversation in chat project.'));
  }
};

export const moveItemToFolderApi = async (folderId, { itemType, itemId }) => {
  try {
    await apiClient.post(`/folders/${encodeURIComponent(folderId)}/items`, {
      ItemType: itemType,
      ItemId: itemId
    });
    return true;
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to move item.'));
  }
};

export const removeItemFromFolderApi = async (folderId, { itemType, itemId }) => {
  try {
    await apiClient.delete(`/folders/${encodeURIComponent(folderId)}/items`, {
      data: { ItemType: itemType, ItemId: itemId }
    });
    return true;
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to remove item from chat project.'));
  }
};
